<template>
  <Transition name="mes-agent-slide">
    <aside
      v-if="panelOpen"
      class="mes-agent-panel"
      :class="{ 'is-resizing': isResizing }"
      :style="{ width: `${panelWidth}px` }"
      aria-label="AX-Agent"
    >
      <div
        class="mes-agent-panel__resize"
        role="separator"
        aria-orientation="vertical"
        aria-label="Agent 패널 너비 조절"
        @pointerdown="beginResize"
      >
        <span />
      </div>

      <header class="mes-agent-panel__header">
        <div class="mes-agent-panel__brand">
          <img :src="agentMark" alt="AX-Agent" class="mes-agent-panel__logo" />
          <div>
            <div class="mes-agent-panel__title">AX-Agent</div>
            <div class="mes-agent-panel__subtitle">말로 묻고, 근거와 함께 확인하는 제조 AI</div>
          </div>
        </div>
        <div class="mes-agent-panel__actions">
          <el-popover v-model:visible="hubOpen" placement="bottom-end" :width="440" trigger="click">
            <template #reference>
              <button type="button" class="mes-agent-panel__hub">
                <span class="mes-agent-panel__hub-dot" />
                Model Hub
              </button>
            </template>
            <div class="mes-agent-hub">
              <div v-if="meta?.llm" class="mes-agent-hub__llm">
                <b>Agent 언어모델</b> {{ meta.llm.provider }} {{ meta.llm.model }}
                <span>질문 이해 · SQL 생성 · 답변 작성 (계산은 분석모델)</span>
              </div>
              <div class="mes-agent-hub__title">
                연결된 분석모델
                <span v-if="Object.keys(algoPrefs).length" class="mes-agent-hub__pinned"
                  >지정 {{ Object.keys(algoPrefs).length }}개</span
                >
                <span
                  >{{ (meta?.models ?? []).filter((m) => m.status === 'connected').length }}개 연결
                  · 칩으로 알고리즘 지정</span
                >
              </div>
              <div
                v-for="m in meta?.models ?? []"
                :key="m.name"
                class="mes-agent-hub__item"
                :class="`is-${m.status}`"
              >
                <div class="mes-agent-hub__name">
                  {{ m.name }}
                  <small v-if="m.version">v{{ m.version }}</small>
                  <el-tag
                    size="small"
                    :type="m.status === 'connected' ? 'success' : 'info'"
                    effect="light"
                    round
                  >
                    {{ m.status === 'connected' ? '연결됨' : '확장 예정' }}
                  </el-tag>
                  <button type="button" class="mes-agent-hub__run" @click="runModelSample(m.name)">
                    ▶ 실행
                  </button>
                </div>
                <div class="mes-agent-hub__desc">{{ m.description }}</div>
                <div v-if="m.algorithms?.length" class="mes-agent-hub__algos">
                  <span class="mes-agent-hub__algos-label">분석 알고리즘</span>
                  <button
                    type="button"
                    class="mes-agent-algo-chip"
                    :class="{ 'is-active': !algoPrefs[m.name] }"
                    @click="setAlgorithm(m.name, null)"
                  >
                    자동
                  </button>
                  <el-tooltip
                    v-for="al in m.algorithms"
                    :key="al.key"
                    :content="al.description"
                    placement="top"
                  >
                    <button
                      type="button"
                      class="mes-agent-algo-chip"
                      :class="{ 'is-active': algoPrefs[m.name] === al.key }"
                      @click="setAlgorithm(m.name, al.key)"
                    >
                      {{ al.name }}
                    </button>
                  </el-tooltip>
                </div>
              </div>
            </div>
          </el-popover>
          <el-tooltip content="대화 지우기" placement="bottom">
            <button
              type="button"
              class="mes-agent-panel__icon"
              :disabled="!messages.length"
              @click="messages = []"
            >
              <el-icon><Delete /></el-icon>
            </button>
          </el-tooltip>
          <button
            type="button"
            class="mes-agent-panel__icon"
            aria-label="Agent 패널 닫기"
            @click="closePanel"
          >
            <el-icon><Close /></el-icon>
          </button>
        </div>
      </header>

      <div ref="scrollRef" class="mes-agent-panel__body">
        <section v-if="!messages.length" class="mes-agent-welcome">
          <div class="mes-agent-welcome__hello">안녕하세요, AX-Agent 입니다.</div>
          <p>
            지금 보고 계신 MES 데이터를 말로 물어보세요. 필요한 데이터를 찾아 조회문을 만들고,
            검증한 뒤 실행하고, 질문에 맞는 분석모델(<b
              >AX-Insight · DocuBrain · Pulse · Optima · Inven</b
            >)을 호출해 결과와 근거를 함께 보여드립니다.
          </p>
          <div class="mes-agent-welcome__label">자주 쓰는 질문</div>
          <div v-for="g in suggestionGroups" :key="g.model" class="mes-agent-welcome__group">
            <div class="mes-agent-welcome__model">
              <span class="mes-agent-welcome__model-name">{{ g.model }}</span>
              <span>{{ g.label }}</span>
            </div>
            <div class="mes-agent-welcome__chips">
              <button
                v-for="q in g.questions"
                :key="q"
                type="button"
                class="mes-agent-chip"
                @click="ask(q)"
              >
                {{ q }}
              </button>
            </div>
          </div>
        </section>

        <template v-for="m in messages" :key="m.id">
          <div v-if="m.role === 'user'" class="mes-agent-msg mes-agent-msg--user">
            <div class="mes-agent-bubble">{{ m.text }}</div>
          </div>
          <div v-else class="mes-agent-msg mes-agent-msg--agent">
            <img :src="agentMark" alt="" class="mes-agent-avatar" />
            <div class="mes-agent-msg__content">
              <div v-if="m.pending" class="mes-agent-progress">
                <div
                  v-for="st in m.liveSteps"
                  :key="`${st.key}-${st.label}-${st.elapsedMs}`"
                  class="mes-agent-progress__step is-done"
                  :class="`is-${st.status}`"
                >
                  <span class="mes-agent-progress__mark">{{
                    st.status === 'done' ? '✓' : '!'
                  }}</span>
                  <span class="mes-agent-progress__label">{{ st.label }}</span>
                  <span class="mes-agent-progress__summary">{{ st.summary }}</span>
                  <span class="mes-agent-progress__ms">{{ fmtMs(st.elapsedMs) }}</span>
                </div>
                <div v-if="m.current" class="mes-agent-progress__step is-active">
                  <span class="mes-agent-progress__mark" />
                  <span class="mes-agent-progress__label">{{ m.current }}</span>
                  <span class="mes-agent-progress__dots">…</span>
                  <span class="mes-agent-progress__ms">{{ fmtMs(m.currentMs) }}</span>
                </div>
                <p v-if="m.streamText" class="mes-agent-progress__text">
                  {{ m.streamText }}<span class="mes-agent-caret" />
                </p>
              </div>
              <el-alert
                v-else-if="m.error"
                :title="m.error"
                type="error"
                show-icon
                :closable="false"
              />
              <MesAgentAnswerCard v-else-if="m.answer" :answer="m.answer" @ask="ask" />
            </div>
          </div>
        </template>
      </div>

      <footer class="mes-agent-panel__footer">
        <div class="mes-agent-conn">
          <el-icon><Connection /></el-icon>
          <span class="mes-agent-conn__name">{{ meta?.connection.system ?? 'AX MES' }}</span>
          <span class="mes-agent-conn__sep">·</span>
          <span>{{ meta?.connection.tenant ?? '' }}</span>
          <span class="mes-agent-conn__sep">·</span>
          <span
            >{{ meta?.connection.engine ?? 'PostgreSQL' }}
            {{ meta?.connection.database ?? '' }}</span
          >
          <el-tag
            v-if="meta?.llm"
            size="small"
            type="warning"
            effect="plain"
            round
            class="mes-agent-conn__mode"
            >LLM {{ meta.llm.model }}</el-tag
          >
          <el-tag
            size="small"
            type="success"
            effect="plain"
            round
            :class="{ 'mes-agent-conn__mode': !meta?.llm }"
            >{{ meta?.connection.mode ?? '읽기 전용' }}</el-tag
          >
        </div>
        <div class="mes-agent-input">
          <el-input
            v-model="draft"
            type="textarea"
            :autosize="{ minRows: 1, maxRows: 4 }"
            resize="none"
            placeholder="예) 최근 30일 날짜별 범위 이탈 횟수 보여줘"
            :disabled="busy"
            @keydown.enter.exact.prevent="submit"
          />
          <el-button
            type="primary"
            :icon="Promotion"
            :loading="busy"
            :disabled="!draft.trim()"
            @click="submit"
            >질문</el-button
          >
        </div>
      </footer>
    </aside>
  </Transition>
</template>

<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { Close, Connection, Delete, Promotion } from '@element-plus/icons-vue';
import { getErrorMessage, useAgentPanel } from '@axsolutionsdev/core-fe';

import {
  type AgentAnswer,
  type AgentMeta,
  type AgentSuggestionGroup,
  type AgentHistoryItem,
  type AgentStep,
  type AgentStreamEvent,
  askAgent,
  askAgentStream,
  fetchAgentMeta,
} from '../../api/agent';
import agentMark from './ax-agent-mark.svg';
import MesAgentAnswerCard from './MesAgentAnswerCard.vue';

interface Message {
  id: number;
  role: 'user' | 'agent';
  text?: string;
  answer?: AgentAnswer;
  error?: string;
  pending?: boolean;
  /** 실시간 진행 — 끝난 단계들 */
  liveSteps: AgentStep[];
  /** 진행 중 단계 라벨 */
  current: string | null;
  currentMs: number;
  /** 스트리밍 중인 답변 문장 */
  streamText: string;
}

const FALLBACK_SUGGESTIONS = [
  '최근 30일 날짜별 범위 이탈 횟수 보여줘',
  '센서별 수집 건수 알려줘',
  '부하전류가 상한을 넘은 시각과 값 보여줘',
  '이번 달 설비별 비가동 시간',
];

const { panelOpen, panelWidth, isResizing, closePanel, beginResize, clampPanelWidthToViewport } =
  useAgentPanel();

const meta = ref<AgentMeta | null>(null);
const suggestionGroups = ref<AgentSuggestionGroup[]>([
  { model: 'AX-Insight', label: '데이터 조회·분석', questions: FALLBACK_SUGGESTIONS },
]);
const hubOpen = ref(false);

/** 모델별 알고리즘 지정 (없으면 자동) — 브라우저에 기억 */
const ALGO_KEY = 'ax-agent-algorithms';
function readAlgoPrefs(): Record<string, string> {
  try {
    const raw = localStorage.getItem(ALGO_KEY);
    const v: unknown = raw ? JSON.parse(raw) : {};
    return v && typeof v === 'object' ? (v as Record<string, string>) : {};
  } catch {
    return {};
  }
}
const algoPrefs = ref<Record<string, string>>(readAlgoPrefs());
function setAlgorithm(model: string, key: string | null): void {
  const next = { ...algoPrefs.value };
  if (key) next[model] = key;
  else delete next[model];
  algoPrefs.value = next;
  try {
    localStorage.setItem(ALGO_KEY, JSON.stringify(next));
  } catch {
    /* 저장 불가 환경 무시 */
  }
}
const messages = ref<Message[]>([]);
const draft = ref('');
const busy = ref(false);
const scrollRef = ref<HTMLElement | null>(null);
let seq = 0;

async function loadMeta(): Promise<void> {
  if (meta.value) return;
  try {
    meta.value = await fetchAgentMeta();
    if (meta.value.suggestionGroups?.length) suggestionGroups.value = meta.value.suggestionGroups;
  } catch {
    /* 메타 실패해도 질문은 가능 */
  }
}

async function scrollToBottom(): Promise<void> {
  await nextTick();
  const el = scrollRef.value;
  if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const fmtMs = (ms: number): string => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}초` : `${ms}ms`);

/** 직전 대화 3건 — "그럼 지난달은?" 같은 후속 질문 해석용 */
function historyOf(): AgentHistoryItem[] {
  const out: AgentHistoryItem[] = [];
  for (let i = 0; i < messages.value.length; i++) {
    const m = messages.value[i];
    const next = messages.value[i + 1];
    if (m?.role === 'user' && next?.answer) {
      const p = next.answer.meta.period;
      out.push({
        question: m.text ?? '',
        intent: next.answer.intent?.key ?? null,
        period: p ? `${p.from}~${p.to}` : null,
      });
    }
  }
  return out.slice(-3);
}

async function ask(question: string): Promise<void> {
  const q = question.trim();
  if (!q || busy.value) return;
  busy.value = true;
  draft.value = '';
  const history = historyOf();
  const blank = { liveSteps: [], current: null, currentMs: 0, streamText: '' };
  messages.value.push({ id: ++seq, role: 'user', text: q, ...blank });
  messages.value.push({ id: ++seq, role: 'agent', pending: true, ...blank });
  const msg = messages.value[messages.value.length - 1]!;
  void scrollToBottom();

  // 서버 이벤트를 큐에 쌓고, 사람이 읽을 수 있는 속도로 화면에 반영
  const queue: AgentStreamEvent[] = [];
  let finished = false;
  let stepStarted = Date.now();
  const clock = setInterval(() => {
    if (msg.current) msg.currentMs = Date.now() - stepStarted;
  }, 100);
  const drain = (async () => {
    while (!finished || queue.length) {
      const ev = queue.shift();
      if (!ev) {
        await sleep(40);
        continue;
      }
      if (ev.type === 'step_start') {
        msg.current = ev.label;
        msg.currentMs = 0;
        stepStarted = Date.now();
        await sleep(160);
      } else if (ev.type === 'step') {
        msg.liveSteps.push(ev.step);
        msg.current = null;
        void scrollToBottom();
        await sleep(ev.step.key === 'answer' ? 0 : 200);
      } else if (ev.type === 'answer_delta') {
        msg.streamText += ev.text;
        await sleep(18);
      } else if (ev.type === 'answer_reset') {
        msg.streamText = ev.text;
      }
    }
  })();

  try {
    let answer: AgentAnswer;
    try {
      answer = await askAgentStream(q, history, (ev) => queue.push(ev), { ...algoPrefs.value });
    } catch (streamErr) {
      // 스트림 연결 자체가 안 되면 일반 호출로 폴백
      if (msg.liveSteps.length) throw streamErr;
      answer = await askAgent(q, { ...algoPrefs.value });
    }
    finished = true;
    await drain;
    msg.answer = answer;
  } catch (err) {
    finished = true;
    msg.error = getErrorMessage(err, 'Agent 응답을 받지 못했습니다.');
  } finally {
    clearInterval(clock);
    msg.pending = false;
    msg.current = null;
    busy.value = false;
    void scrollToBottom();
  }
}

/** Model Hub 에서 모델을 누르면 그 모델의 대표 질문을 바로 실행 */
function runModelSample(model: string): void {
  const q = suggestionGroups.value.find((g) => g.model === model)?.questions[0];
  hubOpen.value = false;
  if (q) void ask(q);
}

function submit(): void {
  void ask(draft.value);
}

watch(panelOpen, (open) => {
  if (open) void loadMeta();
});

onMounted(() => {
  window.addEventListener('resize', clampPanelWidthToViewport);
  if (panelOpen.value) void loadMeta();
});
onUnmounted(() => window.removeEventListener('resize', clampPanelWidthToViewport));
</script>

<style scoped>
.mes-agent-panel {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  min-width: 480px;
  pointer-events: auto;
  border-left: 1px solid var(--el-border-color);
  background: var(--el-bg-color-page, #f7f8fa);
  box-shadow: -4px 0 20px color-mix(in srgb, var(--el-text-color-primary) 10%, transparent);
}
.mes-agent-panel__resize {
  position: absolute;
  top: 0;
  left: -5px;
  z-index: 3;
  width: 10px;
  height: 100%;
  cursor: col-resize;
  touch-action: none;
}
.mes-agent-panel__resize span {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 3px;
  height: 40px;
  border-radius: 999px;
  background: var(--el-border-color);
  transform: translate(-50%, -50%);
}
.mes-agent-panel__resize:hover span,
.mes-agent-panel.is-resizing .mes-agent-panel__resize span {
  background: var(--el-color-primary);
}

.mes-agent-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--el-border-color-lighter);
  background: var(--el-bg-color);
}
.mes-agent-panel__brand {
  display: flex;
  align-items: center;
  gap: 10px;
}
.mes-agent-panel__logo,
.mes-agent-avatar {
  display: block;
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  box-shadow: 0 3px 10px color-mix(in srgb, #10b981 30%, transparent);
}
.mes-agent-avatar {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  box-shadow: 0 2px 6px color-mix(in srgb, #10b981 25%, transparent);
}
.mes-agent-panel__title {
  font-size: 15px;
  font-weight: 700;
  color: var(--el-text-color-primary);
}
.mes-agent-panel__subtitle {
  font-size: 12px;
  color: var(--el-text-color-secondary);
}
.mes-agent-panel__actions {
  display: flex;
  align-items: center;
  gap: 6px;
}
.mes-agent-panel__hub {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid color-mix(in srgb, #f59e0b 45%, transparent);
  border-radius: 999px;
  background: color-mix(in srgb, #f59e0b 8%, transparent);
  font-size: 12px;
  font-weight: 600;
  color: #b45309;
  cursor: pointer;
}
.mes-agent-panel__hub-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 3px color-mix(in srgb, #22c55e 25%, transparent);
}
.mes-agent-panel__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 6px;
  background: var(--el-bg-color);
  color: var(--el-text-color-secondary);
  cursor: pointer;
}
.mes-agent-panel__icon:hover:not(:disabled) {
  border-color: var(--el-border-color);
  color: var(--el-text-color-primary);
}
.mes-agent-panel__icon:disabled {
  opacity: 0.4;
  cursor: default;
}

.mes-agent-hub__llm {
  margin-bottom: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  background: color-mix(in srgb, #16a34a 8%, transparent);
  border: 1px solid color-mix(in srgb, #16a34a 30%, transparent);
  font-size: 12.5px;
  color: var(--el-text-color-primary);
}
.mes-agent-hub__llm span {
  display: block;
  margin-top: 2px;
  font-size: 11.5px;
  color: var(--el-text-color-secondary);
}
.mes-agent-hub__run {
  margin-left: 4px;
  padding: 1px 8px;
  border: 1px solid var(--el-color-primary-light-5);
  border-radius: 999px;
  background: var(--el-color-primary-light-9);
  font-size: 11px;
  font-weight: 600;
  color: var(--el-color-primary);
  cursor: pointer;
}
.mes-agent-hub__run:hover {
  background: var(--el-color-primary);
  color: #fff;
}
.mes-agent-hub__algos {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
}
.mes-agent-hub__algos-label {
  margin-right: 2px;
  font-size: 11px;
  color: var(--el-text-color-secondary);
}
.mes-agent-algo-chip {
  padding: 1px 8px;
  border: 1px solid var(--el-border-color);
  border-radius: 999px;
  background: var(--el-bg-color);
  font-size: 11px;
  color: var(--el-text-color-regular);
  cursor: pointer;
}
.mes-agent-algo-chip:hover {
  border-color: #f59e0b;
}
.mes-agent-algo-chip.is-active {
  border-color: #d97706;
  background: #d97706;
  color: #fff;
}
.mes-agent-hub__pinned {
  color: #b45309 !important;
  font-weight: 600;
}
.mes-agent-hub__title span {
  margin-left: 6px;
  font-size: 11px;
  font-weight: 400;
  color: var(--el-text-color-secondary);
}
.mes-agent-hub__item[role='button'] {
  cursor: pointer;
  transition:
    box-shadow 0.15s,
    transform 0.15s;
}
.mes-agent-hub__item[role='button']:hover {
  box-shadow: 0 2px 10px color-mix(in srgb, #f59e0b 25%, transparent);
  transform: translateY(-1px);
}
.mes-agent-hub__ops {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}
.mes-agent-hub__ops span {
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--el-bg-color);
  border: 1px solid color-mix(in srgb, #f59e0b 35%, transparent);
  font-size: 11px;
  color: #b45309;
}
.mes-agent-welcome__group {
  margin-bottom: 12px;
}
.mes-agent-welcome__model {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}
.mes-agent-welcome__model-name {
  padding: 1px 8px;
  border-radius: 6px;
  background: color-mix(in srgb, #f59e0b 12%, transparent);
  font-weight: 700;
  color: #b45309;
}
.mes-agent-hub__title {
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 700;
}
.mes-agent-hub__item {
  padding: 8px 10px;
  margin-bottom: 6px;
  border: 1px solid color-mix(in srgb, #f59e0b 50%, transparent);
  border-radius: 8px;
  background: color-mix(in srgb, #f59e0b 6%, transparent);
}
.mes-agent-hub__item.is-planned {
  border-style: dashed;
  border-color: var(--el-border-color);
  background: transparent;
  opacity: 0.8;
}
.mes-agent-hub__name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
}
.mes-agent-hub__name small {
  font-weight: 400;
  color: var(--el-text-color-secondary);
}
.mes-agent-hub__name .el-tag {
  margin-left: auto;
}
.mes-agent-hub__desc {
  margin-top: 2px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.mes-agent-panel__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 14px;
  min-height: 0;
  padding: 16px 14px;
  overflow-y: auto;
}
.mes-agent-welcome {
  padding: 16px;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 12px;
  background: var(--el-bg-color);
}
.mes-agent-welcome__hello {
  margin-bottom: 6px;
  font-size: 16px;
  font-weight: 700;
}
.mes-agent-welcome p {
  margin: 0 0 14px;
  font-size: 13.5px;
  line-height: 1.7;
  color: var(--el-text-color-regular);
}
.mes-agent-welcome__label {
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 700;
  color: var(--el-text-color-secondary);
}
.mes-agent-welcome__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.mes-agent-chip {
  padding: 6px 11px;
  border: 1px solid var(--el-border-color);
  border-radius: 999px;
  background: var(--el-bg-color);
  font-size: 13px;
  color: var(--el-text-color-regular);
  cursor: pointer;
  transition:
    border-color 0.15s,
    color 0.15s,
    background 0.15s;
}
.mes-agent-chip:hover {
  border-color: var(--el-color-primary);
  background: var(--el-color-primary-light-9);
  color: var(--el-color-primary);
}

.mes-agent-msg {
  display: flex;
  gap: 8px;
}
.mes-agent-msg--user {
  justify-content: flex-end;
}
.mes-agent-bubble {
  max-width: 80%;
  padding: 9px 13px;
  border-radius: 14px 14px 4px 14px;
  background: var(--el-color-primary);
  font-size: 14px;
  line-height: 1.5;
  color: #fff;
}
.mes-agent-msg__content {
  flex: 1;
  min-width: 0;
  padding: 12px;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 4px 14px 14px 14px;
  background: var(--el-bg-color);
}
.mes-agent-progress {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
}
.mes-agent-progress__step {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--el-text-color-placeholder);
}
.mes-agent-progress__step.is-done {
  color: var(--el-text-color-regular);
}
.mes-agent-progress__step.is-active {
  font-weight: 600;
  color: var(--el-color-primary);
}
.mes-agent-progress__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 1px solid currentColor;
  border-radius: 50%;
  font-size: 10px;
  color: var(--el-color-success);
}
.mes-agent-progress__step.is-active .mes-agent-progress__mark {
  border-color: var(--el-color-primary);
  border-top-color: transparent;
  animation: mes-agent-spin 0.7s linear infinite;
}
.mes-agent-progress__label {
  flex: none;
  font-weight: 600;
}
.mes-agent-progress__summary {
  overflow: hidden;
  color: var(--el-text-color-secondary);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mes-agent-progress__ms {
  margin-left: auto;
  flex: none;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  font-variant-numeric: tabular-nums;
}
.mes-agent-progress__step.is-warn .mes-agent-progress__mark {
  color: var(--el-color-warning);
}
.mes-agent-progress__text {
  margin: 8px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: var(--el-fill-color-extra-light);
  font-size: 14px;
  line-height: 1.7;
  color: var(--el-text-color-primary);
}
.mes-agent-caret {
  display: inline-block;
  width: 7px;
  height: 15px;
  margin-left: 2px;
  vertical-align: -2px;
  background: var(--el-color-primary);
  animation: mes-agent-blink 1s steps(2) infinite;
}
.mes-agent-progress__dots {
  animation: mes-agent-blink 1s steps(2) infinite;
}
@keyframes mes-agent-spin {
  to {
    transform: rotate(360deg);
  }
}
@keyframes mes-agent-blink {
  50% {
    opacity: 0.2;
  }
}

.mes-agent-panel__footer {
  padding: 10px 14px 14px;
  border-top: 1px solid var(--el-border-color-lighter);
  background: var(--el-bg-color);
}
.mes-agent-conn {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 8px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  white-space: nowrap;
  overflow: hidden;
}
.mes-agent-conn__name {
  font-weight: 700;
  color: var(--el-color-primary);
}
.mes-agent-conn__sep {
  color: var(--el-text-color-placeholder);
}
.mes-agent-conn__mode {
  margin-left: auto;
}
.mes-agent-input {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.mes-agent-input :deep(.el-textarea__inner) {
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 14px;
}
.mes-agent-input .el-button {
  height: 36px;
  border-radius: 10px;
}

.mes-agent-slide-enter-active,
.mes-agent-slide-leave-active {
  transition:
    transform 0.22s ease,
    opacity 0.22s ease;
}
.mes-agent-slide-enter-from,
.mes-agent-slide-leave-to {
  transform: translateX(24px);
  opacity: 0;
}
</style>
